import { Message } from "@aws-sdk/client-lex-runtime-v2"
import moment from "moment"
import { Card } from "primereact/card"
import { Fieldset } from "primereact/fieldset"
import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Link, useHistory, useParams } from "react-router-dom"
import MessageList from "../../../../CandidatePortal/scenes/Interview/Chatbot/components/MessageList"

import { AppDispatch } from "../../../../store"
import { activateInterviewByID, retrieveInterview, retrieveRecording } from "../InterviewAction"
import { Interview, InterviewConversation, InterviewItem, InterviewType } from "../interface"
import { Console } from "console"
import { Button } from "primereact/button"
import { ADMIN_ROUTES } from "../../../../constants/routes/admin/menus"
import { InterviewStatusEnum } from "../../../../CandidatePortal/scenes/Interview/Chatbot/interface"
import { CANDIDATE_ROUTES } from "../../../../constants/routes/candidate/routes"
import { Toast } from "primereact/toast"
import { Dialog } from 'primereact/dialog';

interface InterviewViewProps {
}

const InterviewView = (props: InterviewViewProps) => {
  const { } = props
  const { interviewId } = useParams<{ interviewId: string }>()

  const CANDIDATE_URL = process.env.REACT_APP_CANDIDATE_URL
  const MAX_ANALYSIZE_SCORE = 5

  const toast = useRef<any>()
  const history = useHistory()
  const dispatch = useDispatch<AppDispatch>()
  const { interview } = useSelector((state: any) => state.interview)

  const [interviewLink, setInterviewLink] = useState("")

  const [recordingUrl, setRecordingUrl] = useState<string | null>(null);
  const [displayRecordingModal, setDisplayRecordingModal] = useState(false);

  useEffect(() => {
    init()
  }, [])

  const init = () => {
    dispatch(retrieveInterview(interviewId)).unwrap()
    .then((data: Interview) => {
      setInterviewLink(CANDIDATE_URL + CANDIDATE_ROUTES.ROUTE_BEGIN_EXACT_INTERVIEW
        .replace(":interviewId", data.id))
    });
  }
  
  const handleConfigureInterviewFlowBtn = () => {
    history.push(`${ADMIN_ROUTES.ROUTE_INTERVIEWS_ADMIN}/${interview.id}/setup`)
  }

  const handleActivateInterview = () => {
    dispatch(activateInterviewByID(interviewId)).unwrap()
      .then(() => {
        // refetch interview details
        init()

        // show success toast
        toast.current.show({ 
          severity: 'success', 
          summary: 'Activation Success', 
          detail: `Interview successfully activated, candidate can now use the interview link.`, 
          life: 3000 
        })
      })
      .catch((error: any) => {
        console.log(error)
        // show error toast
        toast.current.show({ 
          severity: 'error', 
          summary: 'Activation Error', 
          detail: `Failed to activate interview: ` + error.message, 
          life: 3000 
        })
      })
  };

  const handleRetrieveRecording = (index: number) => {
    dispatch(retrieveRecording({interviewId: interviewId, index: index})).unwrap()
      .then((data: any) => {
        setRecordingUrl(data); // Assuming the response has a 'url' property
        setDisplayRecordingModal(true);
      })
      .catch((error: any) => {
        console.log(error)
        // show error toast
        toast.current.show({ 
          severity: 'error', 
          summary: 'Recording Retrieve Error', 
          detail: `Failed to retrieve recording`, 
          life: 3000 
        })
      })
  };

  const hideRecordingModal = () => {
    setDisplayRecordingModal(false);
    setRecordingUrl(null); // Clear the URL when the modal is closed
  };

  const recordingModalFooter = () => {
    return (
      <div>
        <Button label="Close" icon="pi pi-times" onClick={hideRecordingModal} className="p-button-text" />
      </div>
    );
  };

  return (
    <div>
      <Toast ref={toast} />
      <Card>
        <div className="flex formgrid grid">
          <div className="field col-12">
            <h1>Interview Details</h1>
          </div>

          {interview.type !== InterviewType.APPLICATION &&
            <div className="field col-12">
              {interview.status === InterviewStatusEnum.INACTIVE &&
                <>
                  <Button
                    icon="pi pi-cog"
                    label="Configure Flow"
                    tooltip="Configure a chatbot interview flow for this Interview."
                    tooltipOptions={{ position: 'top' }}
                    className="p-button-secondary mr-2"
                    onClick={() => handleConfigureInterviewFlowBtn()} />
                  <Button
                    icon="pi pi-check"
                    label="Activate Interview"
                    tooltip="Activate this interview."
                    tooltipOptions={{ position: 'top' }}
                    className="p-button-success"
                    onClick={() => handleActivateInterview()} />
                </>
              }
            </div>
          }

          {interview.status === InterviewStatusEnum.ACTIVE &&
            <div className="field col-12">
              <Fieldset toggleable legend="Interview Link">
              <div>
                <a href={interviewLink} target="_blank" rel="noopener noreferrer">{interviewLink}</a>
              </div>
              </Fieldset>
            </div>
          }

          <div className="field col-12">
            <Fieldset toggleable legend={"Interview Details: " + interview.id}>
              <div className="flex formgrid grid">
                <div className="field col-6">
                  Interviewee: {interview.intervieweeName}
                </div>
                <div className="field col-6">
                  E-Mail: {interview.intervieweeEmail}
                </div>
                {interview.application ?
                  <>
                    <div className="field col-6">
                      Job: {interview.application.job.title}
                    </div>
                    <div className="field col-6">
                      Application ID: <Link to={"/applications/" + interview.application.id}>{interview.application.id}</Link>
                    </div>
                  </>
                  :
                  <></>
                }
                <div className="field col-6">
                  Start Date/Time:<br />{moment(interview.startDateTime).format("dddd, Do MMMM YYYY, h:mm:ss a")}
                </div>
                <div className="field col-6">
                  End Date/Time:<br />{moment(interview.endDateTime).format("dddd, Do MMMM YYYY, h:mm:ss a")}
                </div>
                <div className="field col-6">
                  Status: {interview.status}
                </div>
                <div className="field col-6">
                  Type: {interview.type}
                </div>
                <div className="field col-6">
                  Created By: {interview.createdBy}
                </div>
                <div className="field col-6">
                  Created Date: {moment(interview.createdDate).format("Do MMMM YYYY")}
                </div>
              </div>
            </Fieldset>
          </div>

          {interview.interviewResult &&
            <div className="field col-12">
              <Fieldset toggleable legend={"Interview Scorings"}>
                <div className="fluid formgrid grid">
                  <div className="field col-12 md:col-6">
                    <strong>Total Score:</strong>
                    <div>{interview.interviewResult.totalScore} / {interview.interviewResult.totalScoreOutOf} ({interview.interviewResult.totalScorePercentage}%)</div>
                  </div>

                  <div className="field col-12 md:col-6">
                    <strong>Response Score:</strong>
                    <div>{interview.interviewResult.responseScore} / {interview.interviewResult.responseScoreOutOf}</div>
                  </div>

                  <div className="field col-12 md:col-6">
                    <strong>Body Language Score:</strong>
                    <div>
                      {interview.interviewResult.bodyLanguageScore != null
                        ? interview.interviewResult.bodyLanguageScore + " / " + MAX_ANALYSIZE_SCORE
                        : "-"}
                    </div>
                    <small className="text-secondary">
                      {interview.interviewResult.bodyLanguageComments
                        ? interview.interviewResult.bodyLanguageComments
                        : "No comments"}
                    </small>
                  </div>

                  <div className="field col-12 md:col-6">
                    <strong>Dressing Score:</strong>
                    <div>
                      {interview.interviewResult.dressingScore != null
                        ? interview.interviewResult.dressingScore + " / " + MAX_ANALYSIZE_SCORE
                        : "-"}
                    </div>
                    <small className="text-secondary">
                      {interview.interviewResult.dressingComments
                        ? interview.interviewResult.dressingComments
                        : "No comments"}
                    </small>
                  </div>

                  <div className="field col-12 md:col-6">
                    <strong>Energy Level Score:</strong>
                    <div>
                      {interview.interviewResult.energyLevelScore != null
                        ? interview.interviewResult.energyLevelScore + " / " + MAX_ANALYSIZE_SCORE
                        : "-"}
                    </div>
                    <small className="text-secondary">
                      {interview.interviewResult.energyLevelComments
                        ? interview.interviewResult.energyLevelComments
                        : "No comments"}
                    </small>
                  </div>

                  <div className="field col-12 md:col-6">
                    <strong>Eye Contact Score:</strong>
                    <div>
                      {interview.interviewResult.eyeContactScore != null
                        ? interview.interviewResult.eyeContactScore + " / " + MAX_ANALYSIZE_SCORE
                        : "-"}
                    </div>
                    <small className="text-secondary">
                      {interview.interviewResult.eyeContactComments
                        ? interview.interviewResult.eyeContactComments
                        : "No comments"}
                    </small>
                  </div>

                  <div className="field col-12 md:col-6">
                    <strong>Facial Expression Score:</strong>
                    <div>
                      {interview.interviewResult.facialExpressionScore != null
                        ? interview.interviewResult.facialExpressionScore + " / " + MAX_ANALYSIZE_SCORE
                        : "-"}
                    </div>
                    <small className="text-secondary">
                      {interview.interviewResult.facialExpressionComments
                        ? interview.interviewResult.facialExpressionComments
                        : "No comments"}
                    </small>
                  </div>

                  <div className="field col-12 md:col-6">
                    <strong>Speech Clarity Score:</strong>
                    <div>
                      {interview.interviewResult.speechClarityScore != null
                        ? interview.interviewResult.speechClarityScore + " / " + MAX_ANALYSIZE_SCORE
                        : "-"}
                    </div>
                    <small className="text-secondary">
                      {interview.interviewResult.speechClarityComments
                        ? interview.interviewResult.speechClarityComments
                        : "No comments"}
                    </small>
                  </div>
                </div>
              </Fieldset>
            </div>
          }

          {interview.interviewItems &&
            interview.interviewItems.length > 0 &&
            <div className="field col-12">
              <Fieldset toggleable legend={"Interview Results"}>
              {interview.interviewItems.map((item: InterviewItem, index: number) => (
                <Card key={index} className="mb-2">
                  <div className="flex formgrid grid">
                  <div className="field col-12">
                    <b>Question {item.questionNo}: {item.questionName} (Score: {item.score})</b>
                  </div>
                  <div className="field col-12">
                    {item.questionPrompt}
                  </div>
                  <div className="field col-12">
                    <b>Answer:</b> {item.answer}
                  </div>
                  <div className="field col-12">
                    <Button icon="pi pi-video" className="p-button-rounded p-button-secondary ml-2" tooltip="Retrieve Recording" tooltipOptions={{ position: 'bottom' }} onClick={() => handleRetrieveRecording(item.index)} />
                  </div>
                  </div>
                </Card>
              ))}
              </Fieldset>
            </div>
            }

          {interview.interviewConversation &&
            interview.interviewConversation.length > 0 &&
            <div className="field col-12">
              <Fieldset toggleable legend={"Chat History"}>
                <MessageList 
                  conversations={interview.interviewConversation}
                  interviewerName={interview.tenant.interviewerName}
                  handleRetrieveRecording={handleRetrieveRecording} />
              </Fieldset>
            </div>
          }
        </div>
      </Card>

      <Dialog 
        header="Interview Recording" 
        visible={displayRecordingModal} 
        footer={recordingModalFooter()} 
        onHide={hideRecordingModal}
      >
        {recordingUrl && (
          <video width="300px" height="300px" controls>
            <source src={recordingUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        )}
        {!recordingUrl && (
          <div>Loading recording...</div>
        )}
      </Dialog>
    </div>
  )
}

export default InterviewView

